import React, {useCallback, useEffect, useRef} from "react";
import {observer} from "mobx-react";
import styled from "styled-components";
import {useServices} from "../../../../hooks/use-services.hook";
import {useContainerMediaQueriesChecks} from "../../../../hooks/use-container-media-queries-checks.hook";
import {
    IRevolutPromotionalWidgetControllerViewModel
} from "../../../../services/booking/models/payment/methods/revolut/revolut-payment-controller.view-model.interface";
import {
    IRevolutPaymentMethodViewModel
} from "../../../../services/booking/models/payment/methods/revolut/revolut-payment-method.view-model.interface";
import {reaction} from "mobx";


const ContentContainerBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    gap: ${props => props.theme.spacing.spacing16};
`
const ContentTopSectionBox = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${props => props.theme.spacing.flexGap40};
    padding-left: ${props => props.theme.spacing.spacing40};
    width: 80%;
    ${props => props.theme.containerMediaQuery.smallScreen} {
        flex-direction: column-reverse;
        gap: ${props => props.theme.spacing.flexGap8};
        padding-left: 0;
        width: 100%;
    }
`

const ContentTopLeftSectionBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 115px;
`

const ContentTopRightSectionBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`

const RevolutButtonSubtitleBox = styled.div`
    width: 100%;
    align-items: center;
    font-size: ${props => props.theme.fontSize.body2};
    color: ${props => props.theme.colors.mediumTint};
    text-align: center;
`

const ContentBannerBox = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
`
const RevolutBannerBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`

export const RevolutPaymentMethodTabContentComponent: React.FC<{revolutPaymentMethod: IRevolutPaymentMethodViewModel}> = observer((props) => {
    const services = useServices();
    const booking = services.booking.current;
    const bannerContainerRef = useRef<HTMLDivElement | null>(null);
    const containerMediaQuery = useContainerMediaQueriesChecks();
    const controllerRef = useRef<IRevolutPromotionalWidgetControllerViewModel | null>(null);
    const {revolutPaymentMethod} = props;

    const destroyController = useCallback(() => {
        try {
            controllerRef.current?.dispose();
            controllerRef.current = null;
        } catch (err) {
            services.logger.error('Failed to dispose revolutPaymentController', err);
        }
    }, [services.logger]);

    const createController = useCallback(async (containerElement: HTMLDivElement)=> {
        const paymentController = await revolutPaymentMethod.createPromotionalWidgetController();
        controllerRef.current = paymentController;
        paymentController.mount(containerElement);
    }, [revolutPaymentMethod])

    useEffect(() => {
        const reactionDispose = reaction(() => [services.language.currentLanguage, booking.balanceDue],
            async () => {
                if(bannerContainerRef.current) {
                    destroyController();
                    await createController(bannerContainerRef.current);
                }
            })

        return () => reactionDispose();
    }, [destroyController, createController, services.language.currentLanguage, booking.balanceDue]);

    useEffect(() => {
        if(bannerContainerRef.current && !controllerRef.current) {
            createController(bannerContainerRef.current);
        }

        return () => {
            destroyController();
        }

    }, [revolutPaymentMethod, createController, destroyController]);


    if(!services.configuration.data.pspConfigs.Revolut.publicApiKey) {
        return null;
    }

    const renderRevolutBanner = () => {
        const size: {width?: number; height?: number} = {};
        if(containerMediaQuery.smallScreen) {
            size.width = 200;
            size.height = 200;
        }
        return (
            <RevolutBannerBox>
                <img src={revolutPaymentMethod.revolutPointsConfiguration.bannerImageUrl}
                     alt={revolutPaymentMethod.revolutPointsConfiguration.marketingText}
                     {...size}/>
            </RevolutBannerBox>
        )
    }

    return (
        <ContentContainerBox>
            <ContentTopSectionBox>
                <ContentTopLeftSectionBox>
                    <ContentBannerBox ref={bannerContainerRef} />
                </ContentTopLeftSectionBox>
                <ContentTopRightSectionBox>
                    {renderRevolutBanner()}
                </ContentTopRightSectionBox>
            </ContentTopSectionBox>

            <RevolutButtonSubtitleBox dangerouslySetInnerHTML={{
                __html: services.language.translate('Click <b>Revolut</b> button to continue.')
            }}/>

        </ContentContainerBox>
    )
})